import { useReducer, useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  initialLoginState,
  loginReducer,
} from "../features/auth/loginMachine";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ForgotPasswordForm } from "../features/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "../features/auth/ResetPasswordForm";
import { LockedPanel } from "../features/auth/LockedPanel";
import { SuccessPanel } from "../features/auth/SuccessPanel";

export default function Login() {
  const { signIn, session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(loginReducer, initialLoginState);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const idleReason = searchParams.get("reason") === "idle-timeout";
  const resetMode = searchParams.get("mode") === "reset";
  const resetUserId = searchParams.get("userId") ?? "";
  const resetSecret = searchParams.get("secret") ?? "";

  // If we arrived via a password reset link, jump directly to the reset state.
  useEffect(() => {
    if (resetMode && resetUserId && resetSecret) {
      dispatch({ type: "go-reset", userId: resetUserId, secret: resetSecret });
    }
  }, [resetMode, resetUserId, resetSecret]);

  // If already signed in, redirect (declarative — render-time navigate()
  // triggers React 19's "Cannot update a component while rendering" warning).
  if (session && state.state !== "reset") {
    return <Navigate to="/dashboard" replace />;
  }

  async function doSignIn() {
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
      navigate("/dashboard");
    } catch (e) {
      const err = e as Error;
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-cream-200 bg-white p-6 shadow-xs">
        {state.state === "sign-in" && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              doSignIn();
            }}
            aria-live="polite"
          >
            <div className="space-y-1">
              <div className="flex items-center justify-center">
                <img
                  src="/assets/logo.png"
                  alt="PS Neuro Centre logo"
                  className="h-20 w-20 rounded-full object-contain"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              </div>
              <h1 className="text-center font-serif text-2xl text-maroon-700">PS Neuro Centre</h1>
              <p className="text-center text-sm italic text-ink-500">A Centre for Advanced Neurology</p>
              <p className="text-center text-sm text-ink-500">Sign in to your account.</p>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {idleReason && (
              <p className="rounded-md bg-cream-100 px-3 py-2 text-sm text-maroon-700">
                You were signed out after 30 minutes of inactivity. Please sign in again.
              </p>
            )}
            <Button type="submit" disabled={busy || !email || !password} className="w-full">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <button
              type="button"
              className="text-sm text-ink-500 underline"
              onClick={() => dispatch({ type: "go-forgot" })}
            >
              Forgot password?
            </button>
          </form>
        )}

        {state.state === "forgot" && (
          <ForgotPasswordForm
            onCodeSent={(em) => {
              setEmail(em);
            }}
            onSwitch={() => dispatch({ type: "go-sign-in" })}
          />
        )}

        {state.state === "reset" && state.ctx.recoveryUserId && state.ctx.recoverySecret && (
          <ResetPasswordForm
            userId={state.ctx.recoveryUserId}
            secret={state.ctx.recoverySecret}
            onDone={() => dispatch({ type: "success" })}
            onError={(msg) => dispatch({ type: "error", message: msg })}
          />
        )}

        {state.state === "success" && <SuccessPanel />}

        {state.state === "locked" && state.ctx.unlockAt && (
          <LockedPanel
            unlockAt={state.ctx.unlockAt}
            onRetry={() => dispatch({ type: "go-sign-in" })}
          />
        )}
      </div>
    </div>
  );
}
